import useSWR from 'swr'

import { backendFetchResult, backendFetchResultData } from '../common/api'
import { anyWindow, idToGid } from '../common/helpers'
import {
  isLegacyWorkflowActionPrice,
  Workflow,
  WorkflowAction,
  WorkflowAddDiscountDataType,
  WorkflowFilter,
  WorkflowRootFilter,
  WorkflowRootFilterType,
  WorkflowTriggerType,
} from '../common/workflow-models'

export interface WorkflowFormData {
  id?: number
  name: string
  is_active: boolean
  rootFilterType: WorkflowRootFilterType
  triggers: WorkflowTriggerType[]
  filters: WorkflowFilter[]
  actions: WorkflowAction[]
  selling_plan_id?: string | null
  _random?: number
}

export type WorkflowInput = Omit<Workflow, 'id'>

export interface WorkflowInitialParams {
  secondaryPriceAdjustmentSellingPlanId?: string
}

const initialWorkflowFilters = (initialParams: WorkflowInitialParams): WorkflowFilter[] => {
  if (initialParams.secondaryPriceAdjustmentSellingPlanId) {
    return [
      [
        'subscription-contains-any-of-selected-selling-plans',
        [idToGid(initialParams.secondaryPriceAdjustmentSellingPlanId, 'SellingPlan')],
      ] as WorkflowFilter,
    ]
  }

  return []
}

const initialWorkflowTriggers = (initialParams: WorkflowInitialParams): WorkflowTriggerType[] => {
  if (initialParams.secondaryPriceAdjustmentSellingPlanId) {
    return ['subscription-secondary-price-adjustment-applied']
  }

  return []
}

const initialWorkflowName = (initialParams: WorkflowInitialParams): string => {
  if (initialParams.secondaryPriceAdjustmentSellingPlanId) {
    return `Additional future price change actions for selling plan #${initialParams.secondaryPriceAdjustmentSellingPlanId}`
  }

  return ''
}

export const initialWorkflowFormData = (
  initialParams: WorkflowInitialParams = {}
): WorkflowFormData => ({
  name: initialWorkflowName(initialParams),
  is_active: true,
  rootFilterType: 'and',
  triggers: initialWorkflowTriggers(initialParams),
  filters: initialWorkflowFilters(initialParams),
  actions: [],
  selling_plan_id: initialParams.secondaryPriceAdjustmentSellingPlanId
    ? idToGid(initialParams.secondaryPriceAdjustmentSellingPlanId, 'SellingPlan')
    : undefined,
})

export const convertWorkflowToFormData = (workflow: Workflow): WorkflowFormData => {
  const rootFilterType = workflow.filter.length > 1 ? workflow.filter[0] : 'and'
  const filters = workflow.filter.length > 1 ? workflow.filter[1] : []

  return {
    id: workflow.id,
    name: workflow.name || '',
    is_active: workflow.is_active,
    triggers: workflow.triggers,
    filters,
    actions: workflow.actions,
    selling_plan_id: workflow.selling_plan_id,
    rootFilterType,
    _random: Math.random(),
  }
}

const convertFormDataToWorkflowInput = (data: WorkflowFormData): WorkflowInput => {
  const filter: WorkflowRootFilter = [
    data.filters.length > 1 ? data.rootFilterType : 'and',
    data.filters,
  ]

  return {
    name: data.name,
    is_active: data.is_active,
    triggers: data.triggers,
    filter,
    actions: data.actions.map(convertActionFormDataToActionInput),
    selling_plan_id: data.selling_plan_id ?? null,
  }
}

const roundAmount = (amount: string): string =>
  (Math.round(parseFloat(amount) * 100) / 100).toString()

const convertActionFormDataToActionInput = (action: WorkflowAction): WorkflowAction => {
  if (action[0] === 'add-discount' && action[1][2] === 'fixedAmount') {
    let actionDataInput = [...action[1]] as WorkflowAddDiscountDataType
    const amount = action[1][3]
    actionDataInput[3] = isLegacyWorkflowActionPrice(amount)
      ? roundAmount(amount)
      : Object.fromEntries(
          // an empty amount is left as is, so that the backend reports it as missing
          Object.entries(amount).map(([code, val]) => [code, val === '' ? val : roundAmount(val)])
        )
    return [action[0], actionDataInput]
  }
  return action
}

export const fetchWorkflowFormData = async (
  _: any,
  id?: string,
  initialParams: WorkflowInitialParams = {}
) => {
  if (!id) {
    return initialWorkflowFormData(initialParams)
  }

  const workflow = await backendFetchResultData<Workflow>('GET', `/workflows/${id}`)
  const formData = convertWorkflowToFormData(workflow)

  return formData
}

export const useWorkflowSWR = (id: string) =>
  useSWR<WorkflowFormData>(['workflow', id], (_: any, id?: string) => fetchWorkflowFormData(_, id))

export const createOrUpdateWorkflow = async (data: WorkflowFormData, id?: string) => {
  const input = convertFormDataToWorkflowInput(data)
  const method = id ? 'PATCH' : 'POST'
  const url = id ? `/workflows/${id}` : '/workflows'
  const body = JSON.stringify(input)

  return backendFetchResult<Workflow>(method, url, { body })
}

const fetchWorkflows = () => {
  if (anyWindow.newUserTest) {
    return Promise.resolve([])
  }

  return backendFetchResultData<Workflow[]>('GET', '/workflows')
}

export const useWorkflowsSWR = () => useSWR<Workflow[]>(['workflows'], fetchWorkflows)
